import { useQuery } from "@tanstack/react-query";
import React, { PropsWithChildren, ReactNode, useCallback, useContext } from "react";
import { useIntl } from "react-intl";
import { ConditionAPIPromiseClient } from "../gen/witbooking/backoffice/condition/v1/condition_api_grpc_web_pb";
import { GetConditionsRequest } from "../gen/witbooking/backoffice/condition/v1/condition_api_pb";
import { ExtraAPIPromiseClient } from "../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import {
  Extra as ExtraDTO,
  GetAllExtrasRequest,
} from "../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import { getLanguageFromFullLocale } from "../lib/config";
import { useGrpcClient, usePropertyGrpcCall } from "../lib/grpc";
import { Extra, InventoryEntry } from "../lib/model";
import { useInventoryEntriesQuery } from "../modules/inventory/inventory.hook";
import { useProperty } from "./property-provider";

interface PropertyAdminDataProviderProps {
  /**
   * If specified, a loader will be displayed while loading, otherwise, the child component
   * will be rendered directly.
   */
  loader?: ReactNode;
  loadInventories?: boolean;
  loadExtras?: boolean;
  loadConditionLegacyMapping?: boolean;
}

export interface PropertyData {
  inventories?: InventoryEntry[];
  blockingInventoryTickers?: string[];
  extras?: Extra[];
  conditionsInUse?: string[];
  reload: () => void;
}

/**
 * Undefined if it's still loading
 * null if an error occurred while loading.
 */
const propertyDataContext = React.createContext<PropertyData | undefined | null>({
  reload: () => {},
});

function getExtraOption(extra: ExtraDTO, locale: string) {
  const translation =
    extra.getTranslationsList().find(it => it.getLocale() === locale) ??
    extra.getTranslationsList().find(it => it.getLocale() === "es") ??
    extra.getTranslationsList()[0];
  return { name: `${translation.getName()} (${extra.getTicker()})`, ticker: extra.getTicker() };
}

/**
 * Can be used to load property data if the data is needed for a form to render options (i.e. related data)
 *
 * @param children
 * @param loader
 * @param loadInventories
 * @param loadExtras
 * @param loadConditionLegacyMapping
 * @constructor
 */
export function PropertyAdminDataProvider({
  children,
  loader,
  loadInventories,
  loadExtras,
  loadConditionLegacyMapping,
}: PropsWithChildren<PropertyAdminDataProviderProps>) {
  const fullLocale = useIntl().locale;
  const locale = getLanguageFromFullLocale(fullLocale);
  const property = useProperty();
  const conditionsClient = useGrpcClient(ConditionAPIPromiseClient);
  const extraClient = useGrpcClient(ExtraAPIPromiseClient);

  const getConditions = usePropertyGrpcCall(conditionsClient, conditionsClient.getConditions);
  const getExtras = usePropertyGrpcCall(extraClient, extraClient.getAllExtras);

  const loadEnabled = !!(loadInventories || loadExtras || loadConditionLegacyMapping);
  const loadOtherDataEnabled = !!(loadExtras || loadConditionLegacyMapping);

  const inventoriesQuery = useInventoryEntriesQuery(property.ticker, locale, !!loadInventories);

  const loadOtherData = useCallback(async () => {
    const allExtrasPromise = loadExtras ? getExtras(new GetAllExtrasRequest()) : undefined;
    const conditionMappingsPromise = loadConditionLegacyMapping
      ? getConditions(new GetConditionsRequest())
      : undefined;

    const [allExtras, conditionMappings] = await Promise.all([
      allExtrasPromise,
      conditionMappingsPromise,
    ]);

    return {
      extras: allExtras?.getExtrasList().map(v => getExtraOption(v, locale)),
      conditionsInUse: conditionMappings
        ?.getConditionsList()
        .filter(v => v.getInUse())
        .map(v => v.getTicker()),
    };
  }, [loadExtras, loadConditionLegacyMapping, locale, getExtras, getConditions]);

  const {
    data: otherData,
    error: otherError,
    isLoading: otherIsLoading,
    refetch: refetchOtherData,
  } = useQuery({
    queryKey: [
      "propertyExtrasAndConditions",
      property.ticker,
      loadExtras,
      loadConditionLegacyMapping,
      locale,
    ],
    queryFn: loadOtherData,
    enabled: loadOtherDataEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const data: PropertyData | undefined = React.useMemo(() => {
    if (!loadEnabled) {
      return {
        reload: () => {},
      };
    }

    const isLoading =
      (!!loadInventories && inventoriesQuery.isLoading) || (loadOtherDataEnabled && otherIsLoading);
    if (isLoading) return undefined;

    const error = inventoriesQuery.error ?? otherError;
    if (error) {
      console.log("Error loading property data", error);
      return undefined;
    }

    const inventories = loadInventories ? inventoriesQuery.data : undefined;

    return {
      inventories,
      blockingInventoryTickers: inventories
        ?.filter(v => v.blockingType === "OWN")
        .map(v => v.ticker),
      extras: otherData?.extras,
      conditionsInUse: otherData?.conditionsInUse,
      reload: () => {
        if (loadInventories) inventoriesQuery.refetch();
        if (loadOtherDataEnabled) refetchOtherData();
      },
    };
  }, [
    loadEnabled,
    loadInventories,
    inventoriesQuery,
    loadOtherDataEnabled,
    otherIsLoading,
    otherError,
    otherData,
    refetchOtherData,
  ]);

  //Normally, we'd put a loader on these scenarios when we're not loading. However, blocking the whole
  //ui for this datum doesn't seem a good idea. We let the consumer decide.
  return (
    <>
      {data === undefined && loader}
      {(data !== undefined || !loader) && (
        <propertyDataContext.Provider value={data}>{children}</propertyDataContext.Provider>
      )}
    </>
  );
}

export function usePropertyData(): PropertyData | undefined | null {
  return useContext(propertyDataContext);
}

//This one is for compatibility with witadmin
export function withPropertyData<P = {}>(
  Component: React.ComponentType<P & { wbkData?: PropertyData | null }>
): React.ComponentType<P> {
  return (props: P) => {
    const data = usePropertyData();
    return <Component wbkData={data} {...props} />;
  };
}
